//src/pages/AiMiningPage.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { MAIN_API_BASE } from "../config";
import Icon from "../components/icon";
import Modal from "../components/modal";

const fmtUSD = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// The 11 Capital Tiers
const TIERS = [
  { name: "Tier 1", min: 30, max: 499, yield: 2 },
  { name: "Tier 2", min: 500, max: 999, yield: 3 },
  { name: "Tier 3", min: 1000, max: 4999, yield: 5 },
  { name: "Tier 4", min: 5000, max: 9999, yield: 7 },
  { name: "Tier 5", min: 10000, max: 14999, yield: 9 },
  { name: "Tier 6", min: 15000, max: 19999, yield: 12 },
  { name: "Tier 7", min: 20000, max: 49999, yield: 14 },
  { name: "Tier 8", min: 50000, max: 99999, yield: 16 },
  { name: "Tier 9", min: 100000, max: 199999, yield: 18 },
  { name: "Tier 10", min: 200000, max: 499999, yield: 20 },
  { name: "Tier X", min: 500000, max: Infinity, yield: 25 },
];

const getYieldTier = (amount) => {
  if (amount >= 500000) return 25;
  if (amount >= 200000) return 20;
  if (amount >= 100000) return 18;
  if (amount >= 50000) return 16;
  if (amount >= 20000) return 14;
  if (amount >= 15000) return 12;
  if (amount >= 10000) return 9;
  if (amount >= 5000) return 7;
  if (amount >= 1000) return 5;
  if (amount >= 500) return 3;
  if (amount >= 30) return 2;
  return 0;
};

export default function AiMiningPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const [miningCapital, setMiningCapital] = useState(0);
  const [prices, setPrices] = useState({});
  const [walletBalances, setWalletBalances] = useState([]);
  const [modal, setModal] = useState({ open: false, type: "" });
  const [selectedAsset, setSelectedAsset] = useState("USDT");
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [cycleStart, setCycleStart] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ d: 0, h: 0, m: 0, s: 0 });
  const [isPastGracePeriod, setIsPastGracePeriod] = useState(false);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    const amount = parseFloat(withdrawAmount);
    
    if (amount <= 0 || amount > miningCapital) {
      alert(t("invalid_withdraw_amount", "Invalid withdraw amount."));
      setIsProcessing(false);
      return;
    }

    try {
      // Calls your existing withdraw endpoint
      await axios.post(`${MAIN_API_BASE}/earn/withdraw`, { 
        coin: "USDT", 
        amount: amount 
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setMiningCapital(prev => prev - amount);
      setModal({ open: false, type: "" });
      setWithdrawAmount("");
      // Force reload to fetch new cycle logic
      setTimeout(() => window.location.reload(), 1000); 
    } catch (err) {
      alert(err.response?.data?.error || t("withdrawal_failed", "Withdrawal failed."));
    } finally {
      setIsProcessing(false);
    }
  };

  const currentYield = getYieldTier(miningCapital);
  const estWeekly = miningCapital * (currentYield / 100);

// Load Data
  useEffect(() => {
    if (!token) return navigate("/login");
    
    axios.get(`${MAIN_API_BASE}/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setWalletBalances(res.data.assets || [])).catch(() => {});

    axios.get(`${MAIN_API_BASE}/earn/balance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        // FIXED: Check for 'coin' to match the backend response
        const usdt = res.data.assets?.find(a => a.coin === "USDT" || a.symbol === "USDT")?.balance || 0;
        setMiningCapital(Number(usdt));
        setCycleStart(res.data.cycleStart);
      }).catch(() => {});

    const cachedPrices = localStorage.getItem("nc_prices");
    if (cachedPrices) setPrices(JSON.parse(cachedPrices));
  }, [token, navigate]);

  // Live countdown timer from backend cycleStart
  useEffect(() => {
    if (!cycleStart) return;
    
    const timer = setInterval(() => {
      const start = new Date(cycleStart).getTime();
      const end = start + (7 * 24 * 60 * 60 * 1000); // 7 Days
      const diff = end - Date.now();

      // Calculate Grace Period for the UI warning
      const hoursSince = (Date.now() - start) / (1000 * 60 * 60);
      setIsPastGracePeriod(hoursSince > 24);

      if (diff <= 0) {
        setTimeLeft({ d: 0, h: 0, m: 0, s: 0 });
      } else {
        setTimeLeft({
          d: Math.floor(diff / (1000 * 60 * 60 * 24)),
          h: Math.floor((diff / (1000 * 60 * 60)) % 24),
          m: Math.floor((diff / 1000 / 60) % 60),
          s: Math.floor((diff / 1000) % 60)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [cycleStart]);

  const handleAllocate = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    const amount = parseFloat(depositAmount);
    let usdtEquivalent = amount;
    
    // Auto-Convert logic simulation
    if (selectedAsset !== "USDT" && prices[selectedAsset]) {
      usdtEquivalent = amount * prices[selectedAsset];
    }

    // Enforce $50 minimum ONLY if it is a brand new deposit OR they are resetting the cycle
    const isNewCycle = miningCapital === 0 || isPastGracePeriod;
    
    if (isNewCycle && usdtEquivalent < 30) {
      alert(t("min_allocation_30", "Minimum allocation is $30 USDT equivalent."));
      setIsProcessing(false);
      return;
    }

    try {
      // In a real app, this would trigger an auto-convert on the backend first, then allocate.
      await axios.post(`${MAIN_API_BASE}/earn/deposit`, { 
        coin: selectedAsset, 
        amount: amount 
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setMiningCapital(prev => prev + usdtEquivalent);
      setModal({ open: false, type: "" });
      setDepositAmount("");
      // Force reload to grab the correct new timer (if reset)
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      alert(err.response?.data?.error || t("allocation_failed", "Allocation failed."));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-y-auto pb-24 text-white">
      {/* Header */}
      <div className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-white/5 bg-[#0b1020]/80 px-4 backdrop-blur-md">
        <button onClick={() => navigate(-1)} className="p-2 text-slate-400 hover:text-white">
          <Icon name="arrow-left" className="h-6 w-6" />
        </button>
        <h1 className="text-lg font-black tracking-wide text-emerald-400">{t("eth_ai_mining", "ETH AI Mining")}</h1>
        <div className="w-10"></div>
      </div>

      <div className="mx-auto max-w-lg space-y-4 p-4">
        
        {/* Main Capital Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-[#141a2b] to-[#0b1020] p-6 shadow-[0_10px_40px_rgba(16,185,129,0.1)]">
          <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/10 blur-[80px]" />
          
          <div className="relative z-10 flex flex-col items-center text-center">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-emerald-500/70">
              {t("deployed_capital")}
            </div>
            <div className="mt-2 text-[clamp(2.5rem,8vw,3.5rem)] font-black leading-none tracking-tight text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]">
              {fmtUSD(miningCapital)}
            </div>

            {/* AI Status Badge */}
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-400">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400"></span>
              {t("nodes_online", "Nodes Online & Hashing")}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 border-t border-white/5 pt-6">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t("current_yield")}</div>
              <div className="mt-1 text-2xl font-black text-white">{currentYield}% <span className="text-sm text-slate-500">{t("weekly", "Weekly")}</span></div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t("est_payout", "Est. Payout")}</div>
              <div className="mt-1 text-2xl font-black text-emerald-400">+{fmtUSD(estWeekly)}</div>
            </div>
          </div>
        </div>

        {/* Narrative & Analytics Bento Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 rounded-2xl border border-white/5 bg-[#141a2b] p-4">
            <p className="text-xs leading-relaxed text-slate-400">
              <strong className="text-emerald-400">{t("ai_engine_active", "AI Engine Active.")}</strong> {t("ai_engine_desc", "Dynamically allocating capital across top Ethereum Layer-2 nodes. ETH block rewards are automatically liquidated and secured in USDT to protect against market volatility.")}
              <br /><br />
              <span className="text-emerald-500/70 font-bold">{t("rule", "Rule:")}</span> {t("mining_rules_desc", "Deposits added within 24 hours join the current cycle. Deposits added after 24 hours will restart your 7-day timer. Early withdrawals forfeit pending weekly rewards.")}
            </p>
          </div>

          {/* Uptime Block */}
          <div className="rounded-2xl border border-white/5 bg-[#141a2b] p-4">
            <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-500">{t("node_uptime", "Node Uptime")}</div>
            <div className="text-xl font-black text-white">99.98%</div>
            <div className="mt-2 h-1 w-full rounded-full bg-white/5">
              <div className="h-full w-full rounded-full bg-emerald-500"></div>
            </div>
          </div>

          {/* Countdown Block */}
          <div className="rounded-2xl border border-white/5 bg-[#141a2b] p-4 text-right">
            <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-500">{t("next_payout", "Next Payout")}</div>
            <div className="text-lg font-black text-white tabular-nums">
              {timeLeft.d}d {String(timeLeft.h).padStart(2, '0')}:{String(timeLeft.m).padStart(2, '0')}
            </div>
            <div className="mt-1 text-[10px] font-bold text-rose-400">{t("7_day_lock", "7-Day Lock Active")}</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button
            onClick={() => setModal({ open: true, type: "deposit" })}
            className="flex h-14 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-sm font-black text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] transition active:scale-[0.98]"
          >
            <Icon name="zap" className="h-4 w-4" /> {t("deploy_capital", "Deploy Capital")}
          </button>
          
          <button
            onClick={() => setModal({ open: true, type: "withdraw" })}
            className="flex h-14 items-center justify-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 text-sm font-bold text-rose-400 transition hover:bg-rose-500/20 active:scale-[0.98]"
          >
            <Icon name="log-out" className="h-4 w-4" /> {t("withdraw")}
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-white/5 bg-[#141a2b] p-4">
          <h3 className="mb-4 text-xs font-black uppercase tracking-widest text-slate-400">{t("yield_tiers", "Yield Tiers")}</h3>
          <div className="space-y-2">
            {TIERS.map((tier, idx) => {
              const isActive = currentYield === tier.yield;
              return (
                <div 
                  key={idx} 
                  className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-all ${
                    isActive ? "border border-emerald-500/30 bg-emerald-500/10 shadow-[0_0_10px_rgba(16,185,129,0.1)]" : "bg-black/20"
                  }`}
                >
                  <div className="flex flex-col">
                    <span className={`font-bold ${isActive ? "text-emerald-400" : "text-white"}`}>{tier.name}</span>
                    <span className="text-[10px] text-slate-500">
                      {tier.max === Infinity ? `${fmtUSD(tier.min)}+` : `${fmtUSD(tier.min)} - ${fmtUSD(tier.max)}`}
                    </span>
                  </div>
                  <div className={`font-black ${isActive ? "text-emerald-400" : "text-slate-300"}`}>
                    {tier.yield}% <span className="text-[10px] font-normal text-slate-500">{t("per_wk", "/wk")}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Auto-Convert Deposit Modal */}
      {/* Dynamic Action Modal */}
      <Modal visible={modal.open} onClose={() => setModal({ open: false, type: "" })} classWrap="bg-[#0f1424] border border-[#1a2343]">
        
        {/* --- DEPOSIT UI --- */}
        {modal.type === "deposit" && (
          <div className="p-2">
            <h2 className="mb-2 text-center text-xl font-black text-white">{t("allocate_capital", "Allocate Capital")}</h2>
            
            {/* ANTI-EXPLOIT WARNING: Only shows if 24 hours have passed since cycle start */}
            {isPastGracePeriod && miningCapital > 0 ? (
              <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                <strong className="block text-amber-400">{t("notice", "Notice: Cycle Reset")}</strong>
                {t("cycle_reset_desc", "The 24-hour grace period has passed. Adding new funds now will reset your 7-day payout timer for your entire balance.")}
              </div>
            ) : (
              <p className="mb-6 text-center text-xs text-slate-400">{t("deposit_any_asset", "Deposit any asset. It will be auto-converted to USDT to secure your mining contract.")}</p>
            )}

            <form onSubmit={handleAllocate} className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-400">{t("select_asset", "Select Asset")}</label>
                <select
                  value={selectedAsset}
                  onChange={(e) => setSelectedAsset(e.target.value)}
                  className="h-12 w-full appearance-none rounded-xl border border-white/10 bg-black/20 px-4 font-bold text-white outline-none focus:border-emerald-500"
                >
                  {walletBalances.map(b => (
                    <option key={b.symbol} value={b.symbol}>{b.symbol} ({t("avail", "Avail:")} {Number(b.balance).toFixed(4)})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-slate-400">{t("amount", "Amount")}</label>
                <input
                  type="number"
                  step="any"
                  required
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="h-12 w-full rounded-xl border border-white/10 bg-black/20 px-4 font-bold text-white placeholder-slate-600 outline-none focus:border-emerald-500"
                  placeholder={t("min_30", "Min $30")}
                />
                {depositAmount && selectedAsset !== "USDT" && prices[selectedAsset] && (
                  <div className="mt-2 text-right text-xs font-bold text-emerald-400">
                    {t("auto_converts_to", "Auto-Converts to ≈")} {fmtUSD(parseFloat(depositAmount) * prices[selectedAsset])}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className="mt-4 w-full rounded-xl bg-emerald-500 py-4 text-sm font-black text-white transition hover:bg-emerald-600 disabled:opacity-50"
              >
                {isProcessing ? t("allocating_power", "Allocating Node Power...") : t("confirm_allocation", "Confirm Allocation")}
              </button>
            </form>
          </div>
        )}

        {/* --- WITHDRAW UI (PENALTY WARNING) --- */}
        {modal.type === "withdraw" && (
          <div className="p-2">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
              <svg className="h-7 w-7 text-emerald-400" viewBox="0 0 256 417" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M127.961 0L125.166 9.5V285.168L127.961 287.958L255.923 212.32L127.961 0Z" fill="currentColor" fillOpacity="0.9" />
                <path d="M127.962 0L0 212.32L127.962 287.959V0Z" fill="currentColor" />
                <path d="M127.961 312.187L126.386 314.107V412.306L127.961 416.907L256 236.587L127.961 312.187Z" fill="currentColor" fillOpacity="0.9" />
                <path d="M127.962 416.905V312.185L0 236.585L127.962 416.905Z" fill="currentColor" fillOpacity="0.7" />
                <path d="M127.961 287.958L255.921 212.321L127.961 154.159V287.958Z" fill="currentColor" fillOpacity="0.4" />
                <path d="M0 212.32L127.96 287.958V154.159L0 212.32Z" fill="currentColor" fillOpacity="0.6" />
              </svg>
            </div>
            <h2 className="mb-2 text-center text-xl font-black text-white">{t("early_withdrawal", "Early Withdrawal")}</h2>
            
            <div className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
              <p className="mb-3 text-center font-bold text-rose-400">{t("warning_breaking_cycle", "Warning: Breaking the 7-day cycle")}</p>
              <ul className="list-inside list-disc space-y-2 text-xs text-rose-300">
                <li>{t("withdraw_warn_1", "Your primary capital will be instantly returned to your Spot Wallet.")}</li>
                <li><strong className="text-white">{t("withdraw_warn_2", "All accumulated mining profits for this current cycle will be forfeited.")}</strong></li>
                <li>{t("withdraw_warn_3", "When you redeposit, the 7-day timer will restart entirely from Day 1.")}</li>
              </ul>
            </div>

            <form onSubmit={handleWithdraw} className="space-y-4">
              <div>
                <div className="mb-1 flex justify-between text-xs font-bold">
                  <span className="text-slate-400">{t("withdraw_amount_usdt", "Withdraw Amount (USDT)")}</span>
                  <span className="text-emerald-400">{t("available", "Available:")} {fmtUSD(miningCapital)}</span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    required
                    max={miningCapital}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="h-12 w-full rounded-xl border border-white/10 bg-black/20 px-4 font-bold text-white placeholder-slate-600 outline-none focus:border-rose-500"
                    placeholder={t("enter_amount", "Enter amount...")}
                  />
                  <button 
                    type="button"
                    onClick={() => setWithdrawAmount(miningCapital)}
                    className="absolute right-2 top-2 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-white/20"
                  >
                    {t("max", "MAX")}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing || !withdrawAmount}
                className="mt-4 w-full rounded-xl bg-rose-600 py-4 text-sm font-black text-white shadow-[0_0_15px_rgba(225,29,72,0.3)] transition hover:bg-rose-500 disabled:opacity-50"
              >
                {isProcessing ? t("processing", "Processing...") : t("acknowledge_withdraw", "Acknowledge & Withdraw")}
              </button>
            </form>
          </div>
        )}

      </Modal>
    </div>
  );
}
